import React from 'react';
import './styles.scss';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to our error reporting service
    this.setState({
      error,
      errorInfo
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Report to error tracking service (e.g., Sentry, LogRocket)
    this.reportError(error, errorInfo);
  }

  reportError = (error, errorInfo) => {
    try {
      // In a real app, you'd send this to your error tracking service
      const errorReport = {
        id: this.state.errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.props.userId || 'anonymous',
        buildVersion: process.env.REACT_APP_VERSION || 'unknown'
      };

      // For now, we'll just log it. In production, send to your error service
      console.error('Error Report:', errorReport);
      
      // You could send to an API endpoint here:
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport)
      // });

    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI based on error type
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#ff6b6b"/>
              </svg>
            </div>
            
            <h2 className="error-boundary__title">
              {this.props.title || 'Oops! Something went wrong'}
            </h2>
            
            <p className="error-boundary__message">
              {this.props.message || 
                "We're sorry, but something unexpected happened. Our team has been notified and is working to fix this issue."
              }
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary__details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary__error-info">
                  <p><strong>Error:</strong> {this.state.error.message}</p>
                  <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  <pre className="error-boundary__stack">
                    {this.state.error.stack}
                  </pre>
                  <pre className="error-boundary__component-stack">
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </details>
            )}

            <div className="error-boundary__actions">
              <button 
                className="error-boundary__button error-boundary__button--primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              
              <button 
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>

              {this.props.onContactSupport && (
                <button 
                  className="error-boundary__button error-boundary__button--tertiary"
                  onClick={() => this.props.onContactSupport(this.state.errorId)}
                >
                  Contact Support
                </button>
              )}
            </div>

            {this.state.errorId && (
              <p className="error-boundary__error-id">
                Error ID: {this.state.errorId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specific error boundaries for different parts of the app
export const DashboardErrorBoundary = ({ children, userId }) => (
  <ErrorBoundary
    title="Dashboard Error"
    message="There was an issue loading your dashboard. Please try refreshing the page."
    userId={userId}
  >
    {children}
  </ErrorBoundary>
);

export const ChatErrorBoundary = ({ children, userId }) => (
  <ErrorBoundary
    title="Chat Error"
    message="There was an issue with the chat system. Please try again."
    userId={userId}
    fallback={(error, retry) => (
      <div className="chat-error">
        <p>Chat is temporarily unavailable</p>
        <button onClick={retry}>Retry</button>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);

export const ResourceErrorBoundary = ({ children, userId }) => (
  <ErrorBoundary
    title="Resource Error"
    message="There was an issue loading resources. Please try again."
    userId={userId}
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;